import React, { useMemo } from 'react';
import styled from 'styled-components';
import { MenuData } from '../types';
import { getProductLine } from '../lib/catalog';
import { fontStack } from '../lib/fonts';
import { LETTER_HEIGHT_PX, LETTER_WIDTH_PX, PAGE_MARGIN_PX } from '../lib/page';
import { balancedRows, fitFlavorSize, isLightColor } from '../lib/menu';
import { clearTextCache, fitFontSize, longestWordWidth } from '../lib/text';

/** Width inside the half-inch margins. */
const CONTENT_WIDTH = LETTER_WIDTH_PX - 2 * PAGE_MARGIN_PX;
/** Room between two flavor names. */
const NAME_GAP = 30;

const FLOW_LOGO_WHITE = '/images/flow-logo-white.png';
const FLOW_LOGO_BLACK = '/images/flow-logo.png';

// The page is laid out at print size: border-box keeps the margin inside
// the 2550x3300 area that html2canvas captures, so nothing is clipped.
const PreviewContainer = styled.div<{ $font: string; $scale: number }>`
  box-sizing: border-box;
  width: ${LETTER_WIDTH_PX}px;
  height: ${LETTER_HEIGHT_PX}px;
  background: white;
  padding: ${PAGE_MARGIN_PX}px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  font-family: ${props => props.$font} !important;
  font-weight: bold;
  position: relative;
  transform-origin: top left;
  transform: scale(${props => props.$scale});
  overflow: hidden;
  margin-left: 0;
`;

const ConfettiContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
`;

// One static rule for every confetti piece; each piece's size, place, color
// and angle go in its inline style. Styling them through props made
// styled-components inject a new CSS class per piece (hundreds per menu).
const ConfettiPiece = styled.div`
  position: absolute;

  &[data-shape='dots'] {
    border-radius: 50%;
  }

  &[data-shape='stars']:before,
  &[data-shape='hearts']:before {
    position: absolute;
  }

  &[data-shape='stars']:before {
    content: '*';
  }

  &[data-shape='hearts']:before {
    content: '♥';
  }
`;

// A column: header and title at the top, the sections centered in the space
// left, the footer note at the bottom. Auto margins (not justify-content)
// center the sections, so a menu that is too tall overflows at the bottom,
// where the overflow warning measures it, instead of off the top.
const ContentContainer = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const SectionsArea = styled.div`
  flex: none;
  margin: auto 0;
  padding: 10px 0;
`;

const Section = styled.div`
  & + & {
    margin-top: 90px;
  }
`;

const FooterNote = styled.p<{ $fontSize: number; $font: string }>`
  flex: none;
  margin: 30px 0 0;
  font-size: ${props => props.$fontSize}pt;
  font-family: ${props => props.$font} !important;
  font-weight: bold;
  line-height: 1.2;
  text-align: center;
  color: black;
  overflow-wrap: break-word;
`;

const Header = styled.div`
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
`;

const Logo = styled.img`
  display: block;
  margin-right: 20px;
`;

/** Width and height that make the logo's longer side exactly `size`, keeping its shape. */
export const fitLogo = (naturalWidth: number, naturalHeight: number, size: number) => {
  if (!naturalWidth || !naturalHeight) return { width: size, height: size };
  const ratio = size / Math.max(naturalWidth, naturalHeight);
  return { width: Math.round(naturalWidth * ratio), height: Math.round(naturalHeight * ratio) };
};

// The Logo Size slider sets the logo's longer side, for small logos too (a
// 75px logo used to print a quarter inch wide). Sizes are explicit because
// html2canvas does not support object-fit.
const LogoImage: React.FC<{ src: string; size: number }> = ({ src, size }) => {
  const [natural, setNatural] = React.useState<{ w: number; h: number } | null>(null);
  React.useEffect(() => setNatural(null), [src]);
  const { width, height } = natural ? fitLogo(natural.w, natural.h, size) : { width: undefined, height: undefined };
  return (
    <Logo
      src={src}
      alt="Office logo"
      width={width}
      height={height}
      style={natural ? undefined : { maxWidth: size, maxHeight: size }}
      onLoad={e => setNatural({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
    />
  );
};

const LogoPlaceholder = styled.div<{ $size: number }>`
  width: ${props => props.$size}px;
  height: ${props => props.$size}px;
  margin-right: 20px;
  border: 2px dashed #aaa;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${props => Math.max(16, props.$size * 0.15)}px;
  font-weight: bold;
  color: #333;
  text-align: center;
  white-space: pre-line;
  box-sizing: border-box;
`;

// Sized to fit beside the logo (see fitFontSize); a name too long even at
// the smallest size wraps instead of being cut off.
const OfficeName = styled.h1<{ $fontSize: number; $font: string; $wrap: boolean }>`
  min-width: 0;
  font-size: ${props => props.$fontSize}pt;
  font-family: ${props => props.$font} !important;
  margin: 0;
  color: black;
  font-weight: bold;
  line-height: 1.1;
  white-space: ${props => (props.$wrap ? 'normal' : 'nowrap')};
  overflow-wrap: break-word;
`;

const Title = styled.h2<{ $fontSize: number; $font: string }>`
  flex: none;
  font-size: ${props => props.$fontSize}pt;
  font-family: ${props => props.$font} !important;
  text-align: center;
  margin: 20px 0;
  color: black;
  font-weight: bold;
`;

// Banners are centered by their row, not with left:50% + translateX(-50%):
// html2canvas clips a transformed box at its untransformed position, which
// painted a dark block on the right of wide banners in the PDF.
const BannerRow = styled.div`
  display: flex;
  justify-content: center;
  margin: 20px 0;
`;

const CategoryBanner = styled.div<{ $color: string; $styleType: string }>`
  position: relative;
  z-index: 0;
  max-width: 100%;
  box-sizing: border-box;
  text-align: center;

  /* Classic */
  background: ${props => ['classic', 'angled', 'bubble', 'wavy', 'round'].includes(props.$styleType) ? props.$color : 'transparent'};
  padding: ${props => props.$styleType === 'underline' ? '0 10px 18px' : '15px 30px'};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: ${props => props.$styleType === 'round' || props.$styleType === 'bubble' ? '40px' : '4px'};
  box-shadow: ${props => props.$styleType === 'underline' ? 'none' : '0 2px 4px rgba(0,0,0,0.1)'};

  /* side rules only for classic */
  &:before,
  &:after {
    content: '';
    display: ${props => props.$styleType === 'classic' ? 'block' : 'none'};
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 50px;
    height: 10px;
    background: ${props => props.$color};
  }
  &:before { right: calc(100% + 15px); }
  &:after  { left: calc(100% + 15px); }

  /* Angled ribbon */
  ${props => props.$styleType === 'angled' && `
    background: ${props.$color};
    padding: 18px 40px;
    transform: skewX(-15deg);
    & > span { transform: skewX(15deg); }
    &:before, &:after {
      display: block;
      width: 0;
      height: 0;
      top: 0;
      transform: none;
      border-top: 100% solid transparent;
      border-bottom: 100% solid transparent;
    }
    &:before {
      right: 100%;
      border-right: 30px solid ${props.$color};
    }
    &:after {
      left: 100%;
      border-left: 30px solid ${props.$color};
    }
  `}

  /* Bubble (pill) */
  ${props => props.$styleType === 'bubble' && `
    border-radius: 200px;
  `}

  /* Wavy top & bottom */
  ${props => props.$styleType === 'wavy' && `
    clip-path: url(#waveClip);
    -webkit-clip-path: url(#waveClip);
    padding: 60px 60px; /* reduced height by another 20px */
    min-height: 240px;
  `}

  /* Underline variant */
  ${props => props.$styleType === 'underline' && `
    background: transparent;
    &:after {
      content: '';
      position: absolute;
      left: 0;
      bottom: 0;
      width: 100%;
      height: 8px;
      background: ${props.$color};
    }
  `}
`;

const CategoryTitle = styled.h3<{ $fontSize: number; $font: string; $dark?: boolean }>`
  font-size: ${props => props.$fontSize}pt;
  font-family: ${props => props.$font} !important;
  text-align: center;
  margin: 0;
  color: ${props => (props.$dark ? '#000' : '#fff')};
  text-transform: uppercase;
  font-weight: bold;
  padding: 0 20px;
  position: relative;
  z-index: 2;
`;

// The word Flow prints as the Flow logo, as everywhere in Wonderful's UI.
const InlineFlowLogo = styled.img`
  display: inline-block;
  height: 1.3em;
  width: auto;
  margin: 0 0.06em;
  vertical-align: -0.32em;
`;

export const FlowText: React.FC<{ text: string; dark: boolean }> = ({ text, dark }) => {
  const parts = text.split(/\b(flow)\b/i);
  if (parts.length === 1) return <>{text}</>;
  return (
    <>
      {parts.map((part, i) =>
        i % 2 === 1 ? <InlineFlowLogo key={i} src={dark ? FLOW_LOGO_BLACK : FLOW_LOGO_WHITE} alt="Flow" /> : part,
      )}
    </>
  );
};

const FlowBannerLogo = styled.img`
  display: block;
  width: auto;
  margin: 0 20px;
  position: relative;
  z-index: 2;
`;

// Each card is one column; columns never add up to more than the page.
const FlavorGrid = styled.div`
  display: flex;
  flex-wrap: nowrap;
  justify-content: center;
  width: 100%;
  margin: 8px auto;
`;

const FlavorCard = styled.div`
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2px;
  box-sizing: border-box;
`;

// A square box the art is fitted into with explicit sizes: html2canvas
// ignores object-fit, so a non-square Shopify image would print stretched.
const FlavorArtBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  margin-bottom: 3px;
`;

const FlavorArtImage = styled.img`
  display: block;
`;

const FlavorArt: React.FC<{ src: string; alt: string; size: number }> = ({ src, alt, size }) => {
  const [natural, setNatural] = React.useState<{ w: number; h: number } | null>(null);
  React.useEffect(() => setNatural(null), [src]);
  const { width, height } = natural ? fitLogo(natural.w, natural.h, size) : { width: size, height: size };
  return (
    <FlavorArtBox style={{ width: size, height: size }}>
      <FlavorArtImage
        src={src}
        alt={alt}
        width={width}
        height={height}
        crossOrigin="anonymous"
        onLoad={e => setNatural({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
      />
    </FlavorArtBox>
  );
};

// Same box as a mascot image, so emoji flavors line up and never overlap.
const FlavorEmoji = styled.div<{ $size: number }>`
  width: ${props => props.$size}px;
  height: ${props => props.$size}px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${props => Math.round(props.$size * 0.6)}px;
  line-height: 1;
  margin-bottom: 3px;
`;

// Under a flavor that is not in the store yet: a yellow pill like Flow's own
// Coming Soon stickers, readable on any banner color.
const SoonTag = styled.div<{ $fontSize: number; $font: string }>`
  display: inline-block;
  margin-top: 12px;
  padding: 8px 26px 9px;
  border: 4px solid #000;
  border-radius: 999px;
  background: #fff992;
  color: #000;
  font-size: ${props => props.$fontSize}pt;
  font-family: ${props => props.$font} !important;
  font-weight: bold;
  line-height: 1.1;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: nowrap;
`;

const FlavorName = styled.div<{ $fontSize: number; $font: string }>`
  font-size: ${props => props.$fontSize}pt;
  font-family: ${props => props.$font} !important;
  color: black;
  margin-top: 4px;
  line-height: 1.1;
  text-align: center;
  font-weight: bold;
  /* Multi-word names wrap between words; a single long word (Marshmallow)
     stays whole and overflows evenly on both sides instead of breaking. */
  display: flex;
  justify-content: center;

  span {
    text-align: center;
  }
`;

interface MenuPreviewProps {
  data: MenuData;
  /** On-screen zoom; the PDF capture always uses the full-size page. */
  scale?: number;
  /** Object URL of data.logo, owned by the caller. */
  logoUrl?: string;
  /** Called with true when the menu is taller than the page and would be cut off. */
  onOverflowChange?: (overflowing: boolean) => void;
  /** With data.autoFit: called with the art size that fills the page. */
  onFitChange?: (size: number) => void;
}

const outerHeight = (el: HTMLElement) => {
  const style = window.getComputedStyle(el);
  return el.offsetHeight + (parseFloat(style.marginTop) || 0) + (parseFloat(style.marginBottom) || 0);
};

const Confetti: React.FC<{ config: MenuData['confetti'] }> = ({ config }) => {
  const confettiElements = useMemo(() => {
    if (!config.enabled) return [];

    const pieces: JSX.Element[] = [];
    const count = Math.floor(config.density * 20);

    for (let i = 0; i < count; i++) {
      const size = Math.random() * config.size + config.size / 2;
      const color = config.colors[Math.floor(Math.random() * config.colors.length)];
      const style: React.CSSProperties = {
        width: size,
        height: size,
        top: `${Math.random() * 100}%`,
        left: `${Math.random() * 100}%`,
      };
      if (config.shape === 'dots') {
        style.backgroundColor = color;
      } else {
        style.color = color;
        style.fontSize = size;
        style.transform = `rotate(${Math.random() * 360}deg)`;
      }
      pieces.push(<ConfettiPiece key={`confetti-${i}`} data-shape={config.shape} style={style} />);
    }
    return pieces;
  }, [config.enabled, config.density, config.size, config.shape, config.colors]);

  if (!config.enabled) return null;
  return <ConfettiContainer aria-hidden="true">{confettiElements}</ConfettiContainer>;
};

const MenuPreview = React.forwardRef<HTMLDivElement, MenuPreviewProps>(({ data, scale = 0.25, logoUrl, onOverflowChange, onFitChange }, ref) => {
  const font = fontStack(data.font);
  const contentRef = React.useRef<HTMLDivElement>(null);
  const sectionsRef = React.useRef<HTMLDivElement>(null);
  const overflowing = React.useRef(false);

  // Fit to page: the art size that makes the sections fill the free height.
  const [fitted, setFitted] = React.useState<number | null>(null);
  const [fontsReady, setFontsReady] = React.useState(0);
  // Bumped when the header, title or footer change height on their own (a
  // logo finishing loading), which changes the room the sections have.
  const [layoutTick, setLayoutTick] = React.useState(0);
  const imageSize = data.autoFit && fitted ? fitted : data.flavorImageSize;
  const fitPasses = React.useRef({ data, fontsReady, layoutTick, passes: 0 });
  React.useLayoutEffect(() => {
    if (!data.autoFit) return;
    const content = contentRef.current;
    const sections = sectionsRef.current;
    if (!content || !sections || !content.clientHeight) return;
    // Names that wrap at one size and not at the next could flip forever;
    // a few passes per edit always settle.
    // A font that arrives late changes text heights: that measures again too.
    const last = fitPasses.current;
    if (last.data !== data || last.fontsReady !== fontsReady || last.layoutTick !== layoutTick) {
      fitPasses.current = { data, fontsReady, layoutTick, passes: 0 };
    }
    if (fitPasses.current.passes >= 3) return;
    let other = 0;
    Array.from(content.children).forEach(child => {
      if (child !== sections) other += outerHeight(child as HTMLElement);
    });
    const rows = data.categories.map(category => balancedRows(category.flavors));
    const next = fitFlavorSize({
      available: content.clientHeight - other,
      sectionsHeight: sections.offsetHeight,
      currentSize: imageSize,
      rows: rows.reduce((sum, list) => sum + list.length, 0),
      widestRow: Math.max(0, ...rows.map(list => (list[0] ? list[0].length : 0))),
      spacing: data.flavorSpacing,
      pageWidth: content.clientWidth,
    });
    if (next !== null && Math.abs(next - imageSize) >= 4) {
      fitPasses.current.passes += 1;
      setFitted(next);
    }
  }, [data, fontsReady, layoutTick, imageSize]);
  const hasFooter = !!(data.footerNote && data.footerNote.trim());
  React.useEffect(() => {
    const content = contentRef.current;
    if (!data.autoFit || !content || typeof ResizeObserver === 'undefined') return;
    // Not the sections: they resize because of the fit itself.
    const observer = new ResizeObserver(() => setLayoutTick(n => n + 1));
    Array.from(content.children).forEach(child => {
      if (child !== sectionsRef.current) observer.observe(child);
    });
    return () => observer.disconnect();
  }, [data.autoFit, hasFooter]);
  React.useEffect(() => {
    if (data.autoFit && onFitChange) onFitChange(imageSize);
  }, [data.autoFit, imageSize, onFitChange]);
  React.useEffect(() => {
    let active = true;
    const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
    fonts
      ?.ready?.then(() => {
        if (!active) return;
        clearTextCache();
        setFontsReady(n => n + 1);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [data.font]);

  // The page hides anything past its bottom margin, so tell the editor when
  // the content no longer fits instead of silently cutting flavors off.
  React.useLayoutEffect(() => {
    const check = () => {
      const el = contentRef.current;
      if (!el || !onOverflowChange) return;
      const next = el.scrollHeight > el.clientHeight + 1;
      if (next !== overflowing.current) {
        overflowing.current = next;
        onOverflowChange(next);
      }
    };
    check();
    const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
    let active = true;
    fonts?.ready?.then(() => active && check()).catch(() => undefined);
    return () => {
      active = false;
    };
  });

  // Text sized to fit: the office name beside the logo, the title across the
  // page, and flavor names so the longest word of any row fits its column.
  const officeFit = fitFontSize(data.officeName, data.fontSize.officeName, font, CONTENT_WIDTH - data.logoSize - 20);
  // The Flow logo is wider than the word; measure it as four wide letters.
  const titleFit = fitFontSize(data.menuTitle.replace(/\bflow\b/gi, 'WWWW'), data.fontSize.title, font, CONTENT_WIDTH);
  let nameScale = 1;
  for (const category of data.categories) {
    for (const row of balancedRows(category.flavors)) {
      const widest = Math.max(...row.map(f => longestWordWidth(f.name, data.fontSize.flavorName, font)));
      const room = Math.floor(CONTENT_WIDTH / row.length) - NAME_GAP;
      if (widest > room) nameScale = Math.min(nameScale, room / widest);
    }
  }
  const namePt = Math.round(data.fontSize.flavorName * Math.max(0.5, nameScale) * 10) / 10;

  return (
    <PreviewContainer ref={ref} $font={font} $scale={scale}>
      {/* SVG clip-path definition for wavy banners */}
      <svg width="0" height="0" style={{ position: 'absolute' }}>
        <defs>
          <clipPath id="waveClip" clipPathUnits="objectBoundingBox">
            {/* Large scalloped rectangle – waves on all 4 edges (10 crests per side) */}
            <path d="M0,0.12 Q0.05,0 0.10,0.12 Q0.15,0.24 0.20,0.12 Q0.25,0 0.30,0.12 Q0.35,0.24 0.40,0.12 Q0.45,0 0.50,0.12 Q0.55,0.24 0.60,0.12 Q0.65,0 0.70,0.12 Q0.75,0.24 0.80,0.12 Q0.85,0 0.90,0.12 Q0.95,0.24 1,0.12 L1,0.88 Q0.95,1 0.90,0.88 Q0.85,0.76 0.80,0.88 Q0.75,1 0.70,0.88 Q0.65,0.76 0.60,0.88 Q0.55,1 0.50,0.88 Q0.45,0.76 0.40,0.88 Q0.35,1 0.30,0.88 Q0.25,0.76 0.20,0.88 Q0.15,1 0.10,0.88 Q0.05,0.76 0,0.88 Z" />
          </clipPath>
        </defs>
      </svg>
      <Confetti config={data.confetti} />
      <ContentContainer ref={contentRef}>
        <Header>
          {logoUrl ? (
            <LogoImage src={logoUrl} size={data.logoSize} />
          ) : (
            // Only on screen: the PDF leaves the empty box out (see App).
            <LogoPlaceholder $size={data.logoSize} data-preview-only="">{`Your\nLogo\nHere`}</LogoPlaceholder>
          )}
          {data.officeName && (
            <OfficeName $fontSize={officeFit.sizePt} $font={font} $wrap={!officeFit.fits}>
              {data.officeName}
            </OfficeName>
          )}
        </Header>

        <Title $fontSize={titleFit.sizePt} $font={font}>
          <FlowText text={data.menuTitle} dark />
        </Title>

        <SectionsArea ref={sectionsRef}>
          {data.categories.map((category, index) => {
            const darkText = isLightColor(category.color);
            const showSoon = !category.hideComingSoon;
            return (
              <Section key={index}>
                <BannerRow>
                  <CategoryBanner $color={category.color} $styleType={data.bannerStyle || 'classic'}>
                    {category.name.trim().toLowerCase() === 'flow' ? (
                      <FlowBannerLogo
                        src={darkText ? FLOW_LOGO_BLACK : FLOW_LOGO_WHITE}
                        alt="Flow"
                        height={Math.round(data.fontSize.categoryTitle * 1.6)}
                      />
                    ) : (
                      <CategoryTitle $fontSize={data.fontSize.categoryTitle} $font={font} $dark={darkText}>
                        <FlowText text={category.name || getProductLine(category.line).menuLabel} dark={darkText} />
                      </CategoryTitle>
                    )}
                  </CategoryBanner>
                </BannerRow>

                {balancedRows(category.flavors).map((rowFlavors, row) => {
                  // Columns as wide as the art (closer or overlapping with the
                  // Flavor Spacing setting), wider when a name needs it, and
                  // never wider than an equal share of the page.
                  const share = Math.floor(CONTENT_WIDTH / rowFlavors.length);
                  const needed = Math.max(...rowFlavors.map(f => longestWordWidth(f.name, namePt, font))) + NAME_GAP;
                  const pitch = Math.max(1, Math.min(share, Math.max(imageSize + 4 + data.flavorSpacing, needed)));
                  return (
                    <FlavorGrid key={row}>
                      {rowFlavors.map(flavor => (
                        <FlavorCard key={flavor.id} style={{ width: pitch }}>
                          {flavor.image ? (
                            <FlavorArt src={flavor.image} alt={flavor.name} size={imageSize} />
                          ) : (
                            <FlavorEmoji $size={imageSize}>{flavor.emoji}</FlavorEmoji>
                          )}
                          <FlavorName $fontSize={namePt} $font={font} style={{ width: pitch }}>
                            <span>{flavor.name}</span>
                          </FlavorName>
                          {flavor.comingSoon && showSoon && (
                            <SoonTag $fontSize={Math.max(12, Math.round(namePt * 0.55))} $font={font}>
                              Coming Soon
                            </SoonTag>
                          )}
                        </FlavorCard>
                      ))}
                    </FlavorGrid>
                  );
                })}
              </Section>
            );
          })}
        </SectionsArea>
        {data.footerNote && data.footerNote.trim() && (
          <FooterNote $fontSize={Math.max(16, Math.round(data.fontSize.flavorName * 0.9))} $font={font}>
            <FlowText text={data.footerNote.trim()} dark />
          </FooterNote>
        )}
      </ContentContainer>
    </PreviewContainer>
  );
});

export default MenuPreview; 